<script setup lang="ts">
import { computed, ref } from 'vue'
import { describeError } from '../api/http'
import Modal from './Modal.vue'

const props = defineProps<{
  title: string
  label: string
  placeholder?: string
  submitLabel: string
  maxlength?: number
  initial?: string
  /** Shown above the field, e.g. what a deletion loses. */
  message?: string
  /** When set, submitting needs exactly this text typed (destructive actions). */
  confirm?: string
  /** Submitting isn't possible yet, whatever is typed (e.g. no storage to create in). */
  blocked?: boolean
  action: (value: string) => Promise<unknown>
}>()
const emit = defineEmits<{ close: [] }>()

const value = ref(props.initial ?? '')
const busy = ref(false)
const error = ref<string | null>(null)
const ready = computed(
  () => !props.blocked && (props.confirm === undefined ? !!value.value.trim() : value.value.trim() === props.confirm),
)

async function submit(): Promise<void> {
  if (!ready.value) return
  busy.value = true
  error.value = null
  try {
    await props.action(value.value.trim())
    emit('close')
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Modal :title="title" @close="emit('close')">
    <form class="space-y-4" @submit.prevent="submit">
      <p v-if="message" class="text-sm text-text-secondary" data-testid="prompt-message">{{ message }}</p>
      <div>
        <label class="label" for="prompt-input">{{ label }}</label>
        <input
          id="prompt-input"
          v-model="value"
          class="input"
          :placeholder="placeholder"
          :maxlength="maxlength ?? 64"
          autofocus
          data-testid="prompt-input"
        />
      </div>
      <!-- Extra fields, e.g. where a new library is stored -->
      <slot />
      <p v-if="error" class="text-sm text-error">{{ error }}</p>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-ghost" @click="emit('close')">Cancel</button>
        <button
          :class="confirm === undefined ? 'btn-primary' : 'btn-ghost text-error hover:border-signal hover:text-error'"
          :disabled="busy || !ready"
          data-testid="prompt-submit"
        >
          {{ busy ? 'Working…' : submitLabel }}
        </button>
      </div>
    </form>
  </Modal>
</template>
